import type { AxiosError, CanceledError } from 'axios';

type CustomError<T> = Error | AxiosError<T> | CanceledError<T>;

const ErrorCode = {
  Cancelled: 'ERR_CANCELED',
  Forbidden: 'ERR_BAD_REQUEST',
  Unauthorized: 'ERR_BAD_REQUEST',
} as const;

enum ErrorStatus {
  Forbidden = 403,
  Unauthorized = 401,
}

function isEndingWithPeriodOrExclamation(message: string) {
  return message.endsWith('.') || message.endsWith('!');
}

function endWithPeriodOrExclamation(message: string) {
  if (!isEndingWithPeriodOrExclamation(message)) {
    return `${message}.`;
  }

  return message;
}

function getMessageFromError(error: unknown): string {
  if (error instanceof Error) {
    return endWithPeriodOrExclamation(error.message);
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return endWithPeriodOrExclamation(String(error.message));
  }

  if (typeof error === 'string') {
    return endWithPeriodOrExclamation(error);
  }

  return 'An error occurred.';
}

function getIsCancelledError<T>(customError?: CustomError<T> | null) {
  const { code, message } = (customError as CanceledError<T> | null) ?? {};
  return code === ErrorCode.Cancelled || message?.toLowerCase().includes('cancel');
}

function getIsForbiddenError<T>(customError?: CustomError<T> | null) {
  const { status, message } = (customError as AxiosError<T> | null) ?? {};

  return (
    status === ErrorStatus.Forbidden ||
    status === ErrorStatus.Unauthorized ||
    message?.toLowerCase().includes(`${ErrorStatus.Forbidden}`) ||
    message?.toLowerCase().includes(`${ErrorStatus.Unauthorized}`)
  );
}

export { getIsCancelledError, getIsForbiddenError, getMessageFromError };
